/**
 * DailyAssessment — Guided daily self-assessment for one facility area.
 * Photo + Green/Yellow/Red rating + optional note per item.
 * Route: /self-monitoring/daily
 */

import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, Pencil, SkipForward, Trash2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { PhotoReview, type PhotoAnnotation } from '../../components/survey/PhotoReview'
import { useAuth } from '../../context/AuthContext'
import { isFieldAppHost, normalAppUrlForPath } from '../../lib/fieldAppHost'
import { getFacilities, getFacilityFeatureFlags } from '../../lib/services/facilities'
import {
  buildDailyAssessmentConfigFromFeatureFlags,
  getDailyPlan,
  SKIP_REASONS,
  type AssessmentRating,
  type DailyAssessmentAnswer,
  type DailyAssessmentFacilityConfig,
  type DailyAssessmentItem,
  type DailyAssessmentPlan,
  type DailyAssessmentResponse,
  type SkipReason,
  calculateAreaAverage,
} from '../../lib/services/dailyAssessment'
import {
  clearDailyAssessmentDraft,
  loadDailyAssessmentDraft,
  saveDailyAssessmentDraft,
} from '../../lib/offline/dailyAssessmentDraft'

const RESPONSE_LABELS: Record<DailyAssessmentAnswer, string> = {
  yes: 'Yes',
  no: 'No',
  na: 'N/A',
}

const RESPONSE_STYLES: Record<DailyAssessmentAnswer, { selected: string; idle: string }> = {
  yes: {
    selected: 'border-emerald-600 bg-emerald-600 text-white',
    idle: 'border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  },
  no: {
    selected: 'border-amber-600 bg-amber-600 text-white',
    idle: 'border-amber-100 bg-amber-50 text-amber-700 hover:bg-amber-100',
  },
  na: {
    selected: 'border-slate-700 bg-slate-700 text-white',
    idle: 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200',
  },
}

function nextOperatingDailyPlan(date: Date, facilityConfig?: DailyAssessmentFacilityConfig): DailyAssessmentPlan | null {
  for (let offset = 1; offset <= 14; offset++) {
    const nextDate = new Date(date)
    nextDate.setDate(date.getDate() + offset)
    const plan = getDailyPlan(nextDate, [], { facilityConfig })
    if (plan && plan.items.length > 0) return plan
  }

  return null
}

function getDailyPlanForHost(
  date: Date,
  facilityConfig?: DailyAssessmentFacilityConfig,
): { plan: DailyAssessmentPlan | null; isPreview: boolean } {
  const plan = getDailyPlan(date, [], { facilityConfig })
  if (plan || !isFieldAppHost()) return { plan, isPreview: false }

  return {
    plan: nextOperatingDailyPlan(date, facilityConfig),
    isPreview: true,
  }
}

function DailyOverviewLink({
  className,
  children,
}: {
  className: string
  children: string
}) {
  if (isFieldAppHost()) {
    return (
      <a href={normalAppUrlForPath('/dashboard')} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    )
  }

  return (
    <Link to="/" className={className}>
      {children}
    </Link>
  )
}

function dailyAssessmentStatusKey(facilityId: string, date = new Date()): string {
  return `daily-assessment-status:${facilityId}:${date.toISOString().slice(0, 10)}`
}

function writeDailyAssessmentStatus(facilityId: string, status: 'started' | 'finished'): void {
  if (typeof window === 'undefined' || !facilityId) return
  window.localStorage.setItem(dailyAssessmentStatusKey(facilityId), status)
}

type DailyPhotoEvidence = {
  photo_id: string
  size: number
  type: string
  preview_url: string
  annotations: PhotoAnnotation[]
}

function ResponseButton({
  answer,
  selected,
  onSelect,
}: {
  answer: DailyAssessmentAnswer
  selected: boolean
  onSelect: () => void
}) {
  const styles = RESPONSE_STYLES[answer]
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex-1 rounded-xl border-2 px-3 py-3 text-center font-bold transition-all ${
        selected
          ? `${styles.selected} scale-[1.02] shadow-sm`
          : styles.idle
      }`}
    >
      <span className="text-sm">{RESPONSE_LABELS[answer]}</span>
    </button>
  )
}

function responseRating(answer: DailyAssessmentAnswer, severity: AssessmentRating): AssessmentRating {
  if (answer === 'no') return severity
  return 1
}

function hasCommentedAnnotation(evidence: DailyPhotoEvidence | undefined): boolean {
  return Boolean(evidence?.annotations.some((annotation) => annotation.comment.trim().length > 0))
}

function isCompleteResponse(
  response: DailyAssessmentResponse | undefined,
  evidence: DailyPhotoEvidence | undefined,
  item: DailyAssessmentItem | undefined,
): boolean {
  if (!response) return false
  if (response.response === 'no') {
    // A No finding needs a pin with a comment tied to the image — it IS
    // the narrative that feeds report generation and LLM training. Free-
    // text note is optional supplemental context.
    return Boolean(response.photo_id && hasCommentedAnnotation(evidence))
  }
  // Verification items (logs, inventory, physical readings) need a photo
  // on Yes — the photo is the evidence of the check. Pin + comment are
  // not required on Yes because nothing is wrong.
  if (response.response === 'yes' && item?.verification) {
    return Boolean(response.photo_id)
  }
  return true
}

export function DailyAssessmentPage() {
  const { viewerContext } = useAuth()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [currentIdx, setCurrentIdx] = useState(0)
  const [responses, setResponses] = useState<Map<string, DailyAssessmentResponse>>(new Map())
  const [photoEvidence, setPhotoEvidence] = useState<Map<string, DailyPhotoEvidence>>(new Map())
  const [reviewPhoto, setReviewPhoto] = useState<{ itemId: string; photoId: string; url: string } | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [note, setNote] = useState('')
  const [severity, setSeverity] = useState<AssessmentRating>(2)
  const [evidenceError, setEvidenceError] = useState<string | null>(null)
  const [showSkip, setShowSkip] = useState(false)
  const [skipReason, setSkipReason] = useState<SkipReason | ''>('')
  const [skipNote, setSkipNote] = useState('')
  const [isSkipped, setIsSkipped] = useState(false)

  const facilitiesQuery = useQuery({
    queryKey: ['daily-assessment-facilities'],
    queryFn: getFacilities,
    staleTime: 5 * 60 * 1000,
  })
  const selectedFacilityId = viewerContext?.facility_ids?.[0] ?? facilitiesQuery.data?.[0]?.facility_id ?? ''
  const featureFlagsQuery = useQuery({
    queryKey: ['daily-assessment-feature-flags', selectedFacilityId],
    queryFn: () => getFacilityFeatureFlags(selectedFacilityId),
    enabled: selectedFacilityId.length > 0,
    staleTime: 5 * 60 * 1000,
  })
  const facilityConfig = useMemo(
    () => buildDailyAssessmentConfigFromFeatureFlags(featureFlagsQuery.data ?? []),
    [featureFlagsQuery.data],
  )
  const { plan, isPreview } = useMemo(
    () => getDailyPlanForHost(new Date(), facilityConfig),
    [facilityConfig],
  )

  const items = plan?.items ?? []
  const currentItem = items[currentIdx] ?? null
  const currentResponse = currentItem ? responses.get(currentItem.item_id) : undefined
  const answeredCount = Array.from(responses.values())
    .filter((response) => isCompleteResponse(response, photoEvidence.get(response.item_id), items.find((item) => item.item_id === response.item_id))).length
  const progress = items.length > 0 ? Math.round((answeredCount / items.length) * 100) : 0

  const setResponse = useCallback((answer: DailyAssessmentAnswer) => {
    if (!currentItem) return
    writeDailyAssessmentStatus(selectedFacilityId, 'started')
    if (answer !== 'no') {
      setPhotoEvidence(prev => {
        const existing = prev.get(currentItem.item_id)
        if (existing) URL.revokeObjectURL(existing.preview_url)
        const next = new Map(prev)
        next.delete(currentItem.item_id)
        return next
      })
      setReviewPhoto(prev => prev?.itemId === currentItem.item_id ? null : prev)
    }
    setResponses(prev => {
      const next = new Map(prev)
      const existing = prev.get(currentItem.item_id)
      next.set(currentItem.item_id, {
        item_id: currentItem.item_id,
        response: answer,
        rating: responseRating(answer, severity),
        severity: answer === 'no' ? severity : null,
        photo_id: answer === 'no' ? existing?.photo_id ?? null : null,
        note: note || existing?.note || '',
        assessed_at: new Date().toISOString(),
      })
      return next
    })
    setEvidenceError(null)
  }, [currentItem, note, selectedFacilityId, severity])

  const responseAcceptsPhoto = useCallback((item: DailyAssessmentItem | null, resp: DailyAssessmentAnswer | undefined): boolean => {
    if (!item) return false
    if (resp === 'no') return true
    if (resp === 'yes' && item.verification) return true
    return false
  }, [])

  const openPhotoCapture = useCallback(() => {
    if (!responseAcceptsPhoto(currentItem, currentResponse?.response)) return
    photoInputRef.current?.click()
  }, [currentItem, currentResponse?.response, responseAcceptsPhoto])

  const handlePhotoSelected = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    const file = input.files?.[0] ?? null
    if (!responseAcceptsPhoto(currentItem, currentResponse?.response)) {
      input.value = ''
      return
    }
    if (!file) {
      setEvidenceError('No photo received. Try again.')
      input.value = ''
      return
    }
    if (!file.type.startsWith('image/')) {
      setEvidenceError('Photo evidence must be an image file.')
      input.value = ''
      return
    }
    if (file.size < 100) {
      setEvidenceError('Photo appears empty or corrupted. Retake the photo.')
      input.value = ''
      return
    }

    // iOS Safari / Chrome-iOS evict File-backed blob URLs when the native
    // camera hands control back under memory pressure, leaving a dangling
    // object URL that renders as an empty outline in <img src>. Read the
    // File into a base64 data URL synchronously (via FileReader) so the
    // preview survives the camera→app transition. Matches the pattern
    // surveyStore uses internally for IndexedDB-safe photo persistence.
    const itemId = currentItem.item_id
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      const dataUrl = typeof result === 'string' ? result : null
      if (!dataUrl) {
        setEvidenceError('Photo could not be read. Retake the photo.')
        return
      }
      const photo_id = `daily-${itemId}-${Date.now()}`
      setPhotoEvidence(prev => {
        const next = new Map(prev)
        next.set(itemId, {
          photo_id,
          size: file.size,
          type: file.type || 'image/jpeg',
          preview_url: dataUrl,
          annotations: [],
        })
        return next
      })
      setResponses(prev => {
        const next = new Map(prev)
        const existing = prev.get(itemId)
        if (!existing) return next
        next.set(itemId, {
          ...existing,
          photo_id,
          assessed_at: new Date().toISOString(),
        })
        return next
      })
      // Only auto-open PhotoReview when the surveyor is about to record a
      // finding (No answer). For verification Yes the photo IS the evidence;
      // no pin required. Surveyor can still tap the thumbnail to annotate.
      if (currentResponse?.response === 'no') {
        setReviewPhoto({ itemId, photoId: photo_id, url: dataUrl })
      }
      setEvidenceError(null)
    }
    reader.onerror = () => {
      setEvidenceError('Photo could not be read. Retake the photo.')
    }
    reader.readAsDataURL(file)
    input.value = ''
  }, [currentItem, currentResponse?.response, responseAcceptsPhoto])

  const updateSeverity = useCallback((nextSeverity: AssessmentRating) => {
    setSeverity(nextSeverity)
    if (!currentItem || currentResponse?.response !== 'no') return
    setResponses(prev => {
      const next = new Map(prev)
      const existing = prev.get(currentItem.item_id)
      if (!existing) return next
      next.set(currentItem.item_id, {
        ...existing,
        severity: nextSeverity,
        rating: nextSeverity,
        assessed_at: new Date().toISOString(),
      })
      return next
    })
  }, [currentItem, currentResponse?.response])

  const goNext = useCallback(() => {
    if (!currentItem) return
    // Save note if changed
    const existing = responses.get(currentItem.item_id)
    const trimmedNote = note.trim()
    const currentEvidence = photoEvidence.get(currentItem.item_id)
    if (existing?.response === 'no') {
      if (!existing.photo_id) {
        setEvidenceError('No requires a photo before it can be saved.')
        return
      }
      if (!hasCommentedAnnotation(currentEvidence)) {
        // Pin comment is the narrative tied to the exact issue location on
        // the image — mandatory for No because it feeds report generation
        // and LLM training. Free-text note is optional supplemental context.
        setEvidenceError('Drop a pin on the photo and add a comment describing the issue before saving.')
        return
      }
    }
    if (existing?.response === 'yes' && currentItem.verification && !existing.photo_id) {
      setEvidenceError(currentItem.yes_evidence_prompt ?? 'This check requires a photo of the verifying log or reading before it can be saved.')
      return
    }
    if (existing) {
      setResponses(prev => {
        const next = new Map(prev)
        next.set(currentItem.item_id, { ...existing, note: trimmedNote })
        return next
      })
    }
    setNote('')
    setEvidenceError(null)

    if (currentIdx < items.length - 1) {
      setCurrentIdx(currentIdx + 1)
    } else {
      writeDailyAssessmentStatus(selectedFacilityId, 'finished')
      void clearDailyAssessmentDraft(selectedFacilityId)
      setIsComplete(true)
    }
  }, [currentIdx, items.length, note, photoEvidence, responses, currentItem, selectedFacilityId])

  const goPrev = useCallback(() => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1)
    }
  }, [currentIdx])

  // Sync note field when navigating to a different item
  useEffect(() => {
    if (!currentItem) return
    const load = async () => {
      const resp = responses.get(currentItem.item_id)
      setNote(resp?.note ?? '')
      setSeverity(resp?.severity ?? 2)
      setEvidenceError(null)
    }
    load()
  }, [currentIdx, currentItem, responses])

  // Hydrate responses, photo evidence, and position from owner-bound
  // encrypted offline store (#1896 / F-N05). Legacy plaintext localStorage
  // keys are purged on load. Today's date is part of the draft id.
  const hydratedFacilityRef = useRef<string | null>(null)
  useEffect(() => {
    if (!selectedFacilityId) return
    if (hydratedFacilityRef.current === selectedFacilityId) return
    let cancelled = false
    void (async () => {
      const draft = await loadDailyAssessmentDraft(selectedFacilityId)
      if (cancelled) return
      hydratedFacilityRef.current = selectedFacilityId
      if (!draft) return
      // Hydrating persisted draft into React state is the intended effect
      // of this one-shot facility-change handler.
      setResponses(new Map(draft.responses as Array<[string, DailyAssessmentResponse]>))
      setPhotoEvidence(new Map(draft.photoEvidence as Array<[string, DailyPhotoEvidence]>))
      setCurrentIdx(draft.currentIdx)
      setNote(draft.note)
      setSeverity(draft.severity)
    })()
    return () => {
      cancelled = true
    }
  }, [selectedFacilityId])

  // Persist draft on every meaningful change so navigating away +
  // coming back keeps pins, notes, photo data URLs, and position.
  useEffect(() => {
    if (!selectedFacilityId) return
    if (hydratedFacilityRef.current !== selectedFacilityId) return
    if (responses.size === 0 && photoEvidence.size === 0 && currentIdx === 0) return
    void saveDailyAssessmentDraft(selectedFacilityId, {
      currentIdx,
      note,
      severity,
      responses: Array.from(responses.entries()),
      photoEvidence: Array.from(photoEvidence.entries()),
    }).catch(() => {
      /* offline_owner_required / quota — best-effort; no PHI in logs */
    })
  }, [selectedFacilityId, responses, photoEvidence, currentIdx, note, severity])

  const handleSkip = useCallback(() => {
    if (!skipReason || !plan) return
    // In a production build this would persist via edge function.
    // For now, store in component state and show confirmation.
    writeDailyAssessmentStatus(selectedFacilityId, 'finished')
    void clearDailyAssessmentDraft(selectedFacilityId)
    setIsSkipped(true)
    setShowSkip(false)
  }, [skipReason, plan, selectedFacilityId])

  // No plan for today
  if (!plan || items.length === 0) {
    return (
      <div className="mx-auto max-w-lg space-y-3 px-4 py-10 text-center">
        <h1 className="font-brand text-xl font-bold text-[var(--brand-deep)]">No daily walkthrough today</h1>
        <p className="text-sm text-slate-500">
          No daily walkthrough is scheduled for today.
        </p>
        <DailyOverviewLink className="btn-ghost inline-block text-sm">Open full app</DailyOverviewLink>
      </div>
    )
  }

  // Skipped for today
  if (isSkipped) {
    return (
      <div className="mx-auto max-w-lg space-y-5 px-4 py-10 text-center">
        <SkipForward size={48} className="mx-auto text-amber-500" />
        <h1 className="font-brand text-xl font-bold text-[var(--brand-deep)]">Assessment Skipped</h1>
        <p className="text-sm text-slate-600">
          <strong>{plan.combinedAreas.join(' + ')}</strong> skipped for today.
        </p>
        <div className="app-surface p-4 text-left">
          <p className="text-xs font-bold text-slate-500">Reason</p>
          <p className="mt-1 text-sm text-slate-700">{skipReason}</p>
          {skipNote && (
            <>
              <p className="mt-2 text-xs font-bold text-slate-500">Note</p>
              <p className="mt-1 text-sm text-slate-700">{skipNote}</p>
            </>
          )}
        </div>
        <p className="text-xs text-slate-400">
          This skip is recorded for accreditation evidence.
          The area will appear in the next rotation cycle.
        </p>
        <DailyOverviewLink className="btn-primary inline-block px-6 py-2.5 text-sm">
          Back to Overview
        </DailyOverviewLink>
      </div>
    )
  }

  // Completion summary
  if (isComplete) {
    const allResponses = Array.from(responses.values())
    const avg = calculateAreaAverage(allResponses)
    const findings = allResponses.filter(r => r.response === 'no' && r.rating === 3).length
    const concerns = allResponses.filter(r => r.response === 'no' && r.rating === 2).length
    const passed = allResponses.filter(r => r.response === 'yes').length
    const notApplicable = allResponses.filter(r => r.response === 'na').length

    return (
      <div className="mx-auto max-w-lg space-y-5 px-4 py-6">
        <div className="text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
          <h1 className="mt-3 font-brand text-2xl font-bold text-[var(--brand-deep)]">Assessment Complete</h1>
          <p className="mt-1 text-sm text-slate-500">{plan.combinedAreas.join(' + ')}</p>
        </div>

        <div className="app-surface p-5">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="font-brand text-2xl font-extrabold text-emerald-600">{passed}</p>
              <p className="text-xs font-bold text-slate-500">Yes</p>
            </div>
            <div>
              <p className="font-brand text-2xl font-extrabold text-amber-600">{concerns}</p>
              <p className="text-xs font-bold text-slate-500">Concerns</p>
            </div>
            <div>
              <p className="font-brand text-2xl font-extrabold text-rose-600">{findings}</p>
              <p className="text-xs font-bold text-slate-500">Findings</p>
            </div>
          </div>
          {notApplicable > 0 && (
            <p className="mt-3 text-center text-xs font-semibold text-slate-500">
              {notApplicable} N/A response{notApplicable === 1 ? '' : 's'} recorded.
            </p>
          )}
          <div className="mt-4 text-center">
            <p className="text-xs font-bold text-slate-500">Average Rating</p>
            <p className={`font-brand text-3xl font-extrabold ${avg <= 1.3 ? 'text-emerald-600' : avg <= 2 ? 'text-amber-600' : 'text-rose-600'}`}>
              {avg.toFixed(1)}
            </p>
          </div>
        </div>

        <DailyOverviewLink className="btn-primary block w-full py-3 text-center text-sm">
          Back to Overview
        </DailyOverviewLink>
      </div>
    )
  }

  // Assessment in progress
  return (
    <FieldAppShell
      eyebrow="Daily Walkthrough"
      title="Daily Assessment"
      subtitle={plan.combinedAreas.join(' + ')}
      progress={progress}
      progressLabel={`${currentIdx + 1} of ${items.length} items`}
      backTo="/"
      // If the surveyor is mid-walkthrough, back arrow steps one item
      // backwards instead of exiting. On the first item it falls through
      // to backTo="/" so a dedicated exit still works.
      onBack={currentIdx > 0 ? goPrev : undefined}
      backLabel={currentIdx > 0 ? 'Previous item' : 'Back to overview'}
      showBackControl={!isFieldAppHost() || currentIdx > 0}
      showNormalAppReturn
      contentClassName="mx-auto max-w-lg space-y-4 px-4 py-4 pb-28"
      rightSlot={(
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-400">{currentIdx + 1}/{items.length}</span>
          <button
            type="button"
            onClick={() => setShowSkip(true)}
            className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-amber-300 hover:text-amber-600"
            title="Skip today"
          >
            <SkipForward size={14} />
          </button>
        </div>
      )}
    >
      {reviewPhoto && (
        <PhotoReview
          key={reviewPhoto.photoId}
          photoUrl={reviewPhoto.url}
          annotations={photoEvidence.get(reviewPhoto.itemId)?.annotations ?? []}
          // Pin + comment is the narrative for a No finding (feeds report
          // generation + LLM training). Verification-Yes only needs the
          // photo — surveyors can skip pinning.
          requireCommentedPin={responses.get(reviewPhoto.itemId)?.response === 'no'}
          onSave={(annotations) => {
            setPhotoEvidence(prev => {
              const next = new Map(prev)
              const existing = next.get(reviewPhoto.itemId)
              if (existing && existing.photo_id === reviewPhoto.photoId) {
                next.set(reviewPhoto.itemId, { ...existing, annotations })
              }
              return next
            })
            setReviewPhoto(null)
            setEvidenceError(null)
          }}
          onClose={(annotations) => {
            // Preserve whatever pins the user placed, even on dismiss — see
            // PhotoReview.onClose docstring. requireCommentedPin is still
            // enforced higher up by "Save No Finding & Next".
            setPhotoEvidence(prev => {
              const next = new Map(prev)
              const existing = next.get(reviewPhoto.itemId)
              if (existing && existing.photo_id === reviewPhoto.photoId) {
                next.set(reviewPhoto.itemId, { ...existing, annotations })
              }
              return next
            })
            setReviewPhoto(null)
          }}
        />
      )}

      {isPreview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          No daily walkthrough is scheduled for today. Previewing the next scheduled walkthrough.
        </div>
      )}

      {/* Plan meta — domain cards + data-point count + estimated time (#1033 AC) */}
      {plan && items.length > 0 && (
        <section
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          data-testid="daily-plan-meta"
          aria-label="Today's daily assessment plan"
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Today&apos;s walkthrough
          </p>
          <p className="mt-1 text-sm font-extrabold text-[var(--brand-deep)]">
            {plan.combinedAreas.join(' + ')}
          </p>
          <p className="mt-1 text-xs font-semibold text-slate-600">
            Phone-first checklist using the same Yes / No / N/A interaction family as Guided Survey.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="daily-plan-domains">
            {plan.domains.map((domain) => {
              const domainCount = items.filter((item) => item.domain === domain).length
              return (
                <span
                  key={domain}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-light)] px-2.5 py-1 text-[11px] font-bold text-[var(--brand-primary)]"
                  data-testid={`daily-domain-card-${domain}`}
                >
                  <span>{domain}</span>
                  <span className="rounded bg-white/80 px-1.5 py-0.5 text-[10px] font-extrabold text-slate-600">
                    {domainCount} pt{domainCount === 1 ? '' : 's'}
                  </span>
                </span>
              )
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs font-bold text-slate-600">
            <span data-testid="daily-plan-item-count">{items.length} data points</span>
            <span data-testid="daily-plan-estimated-minutes">~{plan.estimatedMinutes} min</span>
          </div>
        </section>
      )}

      {/* Skip Modal */}
      {showSkip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-sm font-bold text-slate-900">Skip Today's Assessment?</h3>
            <p className="mt-1 text-xs text-slate-500">
              Select a reason. This is recorded for accreditation evidence.
            </p>
            <select
              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value as SkipReason)}
            >
              <option value="">Select reason...</option>
              {SKIP_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Additional note (optional)"
              value={skipNote}
              onChange={(e) => setSkipNote(e.target.value)}
              maxLength={200}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setShowSkip(false)}
                className="btn-ghost flex-1 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={!skipReason}
                className="flex-1 rounded-xl bg-amber-500 py-2 text-sm font-bold text-white disabled:opacity-40"
              >
                Skip Today
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Item */}
      {currentItem && (
        <>
          <div className="app-surface p-5">
            <div className="mb-1 flex items-center gap-2">
              <span className="rounded bg-[var(--brand-light)] px-2 py-0.5 text-[10px] font-extrabold text-[var(--brand-primary)]">
                {currentItem.domain}
              </span>
              <span className="text-[10px] font-semibold text-slate-400">{currentItem.standard_ref}</span>
            </div>
            <p className="mt-2 text-sm font-bold leading-relaxed text-[var(--brand-deep)]">
              {currentItem.label}
            </p>
            {currentItem.control_type === 'operational' && (
              <p className="mt-2 text-xs font-semibold text-slate-500">
                Facility-configured daily control
              </p>
            )}
          </div>

          {/* Response */}
          <div className="flex gap-2">
            <ResponseButton answer="yes" selected={currentResponse?.response === 'yes'} onSelect={() => setResponse('yes')} />
            <ResponseButton answer="no" selected={currentResponse?.response === 'no'} onSelect={() => setResponse('no')} />
            <ResponseButton answer="na" selected={currentResponse?.response === 'na'} onSelect={() => setResponse('na')} />
          </div>

          {((currentResponse?.response === 'no') || (currentResponse?.response === 'yes' && currentItem.verification)) && (
            <div className="app-surface space-y-3 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {currentResponse?.response === 'no' ? 'No Response Evidence' : 'Verification Photo'}
                </span>
                <span className="text-[10px] font-semibold text-slate-400">{currentItem.domain}</span>
              </div>
              {currentResponse?.response === 'yes' && currentItem.verification && currentItem.yes_evidence_prompt && (
                <p className="text-xs text-slate-600">{currentItem.yes_evidence_prompt}</p>
              )}
              {!photoEvidence.get(currentItem.item_id) && (
                <button
                  type="button"
                  onClick={openPhotoCapture}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-4 text-sm font-bold text-slate-500 transition-colors hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                >
                  <Camera size={18} />
                  Take Photo
                </button>
              )}
              {/* Omit `capture="environment"` so desktop browsers show their
                  normal file picker (Mac Safari/Chrome would otherwise demand
                  a rear camera and no-op the click). iOS/Android still offer
                  "Take Photo" as one option inside their native file picker
                  when `accept="image/*"` is set, so mobile UX is unchanged. */}
              <input
                ref={photoInputRef}
                aria-label="Daily evidence photo"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handlePhotoSelected}
              />
              {(() => {
                const evidence = photoEvidence.get(currentItem.item_id)
                if (!evidence) return null
                const hasComment = hasCommentedAnnotation(evidence)
                const itemId = currentItem.item_id
                const openReview = () => {
                  setReviewPhoto({
                    itemId,
                    photoId: evidence.photo_id,
                    url: evidence.preview_url,
                  })
                }
                const removePhoto = () => {
                  if (typeof window !== 'undefined' && !window.confirm('Remove this photo and any pin comments? This cannot be undone.')) return
                  setPhotoEvidence(prev => {
                    const next = new Map(prev)
                    next.delete(itemId)
                    return next
                  })
                  setResponses(prev => {
                    const next = new Map(prev)
                    const existing = prev.get(itemId)
                    if (existing) next.set(itemId, { ...existing, photo_id: null })
                    return next
                  })
                  setReviewPhoto(prev => (prev?.itemId === itemId ? null : prev))
                  setEvidenceError(null)
                }
                return (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={openReview}
                          className="relative group block rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-50 shadow-sm hover:border-[var(--brand-primary)] transition-colors"
                          title="Tap to annotate"
                          aria-label="Review captured photo"
                        >
                          <img
                            src={evidence.preview_url}
                            alt="Captured"
                            className="w-16 h-16 object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <Pencil size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={removePhoto}
                          aria-label="Remove captured photo"
                          title="Remove photo"
                          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-red-500 shadow-sm hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {/* "Add Another" is currently a replace path because
                          DailyPhotoEvidence holds one photo per item. A
                          true multi-photo rewrite is tracked as a follow-up
                          plan in docs/PROGRESS.md. Keep the label wording
                          honest ("Replace") so surveyors know the current
                          contract; flip to "Add" + grid when multi lands. */}
                      <button
                        type="button"
                        onClick={openPhotoCapture}
                        className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                      >
                        <Camera size={14} />
                        Replace
                      </button>
                    </div>
                    {!reviewPhoto && (
                      <p className={`text-xs font-semibold ${
                        currentResponse?.response === 'yes'
                          ? 'text-emerald-700'
                          : hasComment ? 'text-emerald-700' : 'text-amber-700'
                      }`}>
                        {currentResponse?.response === 'yes'
                          ? 'Verification photo attached.'
                          : hasComment
                            ? 'Photo pin comment ready to attach.'
                            : 'Photo captured. Tap the thumbnail to drop a pin on the issue and add a comment.'}
                      </p>
                    )}
                  </div>
                )
              })()}
              {currentResponse?.response === 'no' && (
                <>
                  <input
                    type="text"
                    aria-label="No response note"
                    placeholder={hasCommentedAnnotation(photoEvidence.get(currentItem.item_id))
                      ? 'Optional — pin comment already captures the narrative'
                      : 'Describe what needs follow-up'}
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value)
                      setEvidenceError(null)
                    }}
                    maxLength={200}
                    className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <div className="space-y-1.5">
                    <span className="text-xs text-slate-500">Severity:</span>
                    <div className="flex gap-2">
                      {([1, 2, 3] as const).map((level) => {
                        const labels = { 1: 'Low', 2: 'Moderate', 3: 'High' } as const
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => updateSeverity(level)}
                            className={`flex-1 rounded-lg py-2 text-xs font-bold transition-colors ${
                              severity === level
                                ? 'bg-amber-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {level} - {labels[level]}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
              {evidenceError && <p className="text-xs font-semibold text-red-600">{evidenceError}</p>}
            </div>
          )}

          {currentResponse?.response === 'na' && (
            <input
              type="text"
              placeholder="Quick note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          )}

          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={currentIdx === 0}
              className="btn-ghost flex-1 py-2.5 text-sm disabled:opacity-40"
            >
              <ArrowLeft size={14} className="mr-1 inline" /> Previous
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!currentResponse?.response}
              className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-40"
            >
              {currentIdx === items.length - 1 ? 'Complete' : 'Next'}
              {currentIdx < items.length - 1 && <ArrowRight size={14} className="ml-1 inline" />}
            </button>
          </div>
        </>
      )}
    </FieldAppShell>
  )
}
